import type { ProjectState } from "./types";
import { DEFAULT_SKILL_IDS } from "./skill-catalog";

const now = () => new Date().toISOString();

export function createInitialState(): ProjectState {
  const timestamp = now();
  return {
    version: 1,
    id: crypto.randomUUID(),
    projectName: "未命名研究计划",
    idea: "",
    venueUrls: "",
    venueManualText: "",
    venueSources: [],
    profile: {
      domain: "",
      experience: "",
      strengths: "",
      priorAssets: "",
      avoidTopics: "",
      compute: "",
      modelAccess: "",
      dataAccess: "",
      budget: "",
      deadline: "",
      riskTolerance: "balanced",
      preferredContributions: ["new_problem", "new_mechanism"],
    },
    interviewAnswers: {},
    directions: [],
    selectedDirectionId: "",
    experiment: {
      hypothesis: "",
      taskDefinition: "",
      implementationStack: "",
      runMatrix: "",
      datasets: "",
      evaluationProtocol: "",
      primaryMetric: "",
      secondaryMetrics: "",
      baselines: "",
      mainComparison: "",
      ablations: "",
      hyperparameters: "",
      robustness: "",
      caseStudy: "",
      seeds: "至少 3 个固定随机种子；若领域惯例不报告方差，则解释并保持主表一致",
      computeBudget: "",
      artifactPlan:
        "configs/ 保存冻结配置；runs/ 保存运行 manifest；results/raw/ 保存不可修改的逐样本记录；results/processed/ 保存由脚本生成的汇总表",
      reportingPlan:
        "主表回答核心研究问题；消融表解释机制；鲁棒性、效率与案例只在能排除替代解释时进入正文",
      stopCriteria: "核心主张得到预注册指标支持；结果、日志和绘图输入已冻结",
      pivotCriteria: "两轮合理实现与调参后仍无机制性增益，或公平对比无法成立时回到选题阶段",
      requiredEvidence: [
        "main",
        "ablation",
        "robustness",
        "hyperparameter",
        "case",
        "efficiency",
      ],
    },
    paper: {
      workingTitle: "",
      centralClaim: "",
      contributions: "",
      narrative: "",
      sections:
        "Abstract; Introduction; Related Work; Problem Formulation; Method/System; Experiments; Limitations/Ethics; Conclusion",
      citationRules:
        "所有引用必须真实可检索；优先一手论文与官方标准；一句通常承载 1–2 个直接相关引用；建立 claim–evidence ledger",
      figureSkill: "$sci-diagram-drawing",
      figures: [
        {
          id: "intro",
          role: "Introduction problem figure",
          message: "让跨方向审稿人一眼看懂问题、失败后果与研究必要性",
          evidence: "仅表达已定义的问题和真实失败模式，不暗示尚未验证的结果",
          format: "editable + PDF + PNG preview",
        },
        {
          id: "framework",
          role: "Method/framework overview",
          message: "展示输入、核心机制、约束/门控与输出之间的因果关系",
          evidence: "图中每个模块必须能在方法或消融中找到对应证据",
          format: "editable + PDF + PNG preview",
        },
      ],
      reviewRounds:
        "先完成自检，再进行独立评审；优先修复多个审稿人共同指出且会影响正确性的事项，保留变更记录",
      writingConstraints:
        "自然、克制、证据先于形容词；不把征稿评价标准写成生硬口号；摘要和引言从问题、缺口、机制与结果出发",
    },
    promptMode: "master",
    refinedPrompts: {},
    selectedSkills: [...DEFAULT_SKILL_IDS],
    stageNotes: {},
    referencePapers: [],
    memory: {
      contextBudget: 60000,
      capsules: [],
    },
    workspace: {
      absolutePath: "",
      handleName: "",
    },
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export function createFictionalDemoState(): ProjectState {
  const state = createInitialState();
  state.projectName = "虚构示例：温室巡检 Agent 研究计划";
  state.idea =
    "让缺少自动化经验的小型温室也能使用维护 Agent：它需要把传感器告警、设备手册和工单约束转化为可核验的巡检建议。";
  state.venueUrls = "";
  state.venueManualText =
    "【虚构演示材料】Orion Applied Agents Symposium 20XX。应用系统赛道关注可部署的智能体、清晰的运行边界、可复现实验与风险治理。篇幅、截止日期和投稿格式均为演示占位信息，使用前必须替换为目标 venue 的官方原文。";
  state.venueSources = [
    {
      id: crypto.randomUUID(),
      url: "local://fictional-demo",
      title: "虚构征稿材料，仅用于产品演示",
      excerpt: state.venueManualText,
      text: state.venueManualText,
      fetchedAt: now(),
      status: "ready",
    },
  ];
  state.venueBrief = {
    venueName: "Orion Applied Agents Symposium 20XX（虚构）",
    track: "Applied Systems Track（虚构）",
    summary:
      "该虚构赛道用于演示如何把应用需求、部署约束、可复现证据和风险治理转化为研究计划，不对应任何真实会议。",
    facts: [
      {
        label: "投稿时间",
        value: "演示占位，尚未核验",
        evidenceUrl: "local://fictional-demo",
        confidence: "low",
      },
      {
        label: "主题匹配",
        value: "Applied agent systems（虚构）",
        evidenceUrl: "local://fictional-demo",
        confidence: "low",
      },
      {
        label: "复现要求",
        value: "计划提供代码、配置和冻结结果；真实要求待核验",
        evidenceUrl: "local://fictional-demo",
        confidence: "low",
      },
    ],
    unknowns: ["真实 venue、截止日期、模板、篇幅、匿名政策和 artifact 规则"],
    warnings: [
      "这是一份虚构示例，不得把其中任何占位要求用于真实投稿",
    ],
    questions: [],
  };
  state.profile = {
    domain: "智能体系统与轻量级农业运维",
    experience: "会使用 Python 和常见 LLM API，尚未搭建完整科研流水线",
    strengths: "理解应用现场，能够整理设备手册与历史工单",
    priorAssets: "一台普通开发电脑；可公开的合成告警规则",
    avoidTopics: "避免依赖专有生产数据；避免把模型规模当作唯一贡献",
    compute: "单张消费级 GPU 或按量 API",
    modelAccess: "OpenAI-compatible API 与可选本地模型",
    dataAccess: "公开设备文档与后续构建的合成测试集",
    budget: "先完成低成本可行性实验，再决定是否扩大评测",
    deadline: "尚未确定",
    riskTolerance: "conservative",
    preferredContributions: ["new_problem", "systems", "efficiency"],
  };
  state.directions = [
    {
      id: "fictional-greenhouse-agent",
      title: "约束感知的温室巡检 Agent（虚构计划）",
      oneLine: "把告警、手册证据和操作权限联合起来，生成可追溯的巡检建议。",
      researchQuestion:
        "在信息不完整且操作权限受限时，显式证据与约束检查能否减少不可执行建议？",
      unresolvedGap:
        "通用聊天式 Agent 容易忽略设备前置条件，也很难说明建议依据。",
      proposedMechanism:
        "检索证据卡、约束校验器与拒答/转人工门控组成的轻量工作流。",
      benchmarkPlan:
        "先冻结一套合成故障场景；数据、指标和可比基线由正式文献调研后确定。",
      primaryMetric: "可执行建议率（定义待正式协议核验）",
      baselines: ["直接提示", "检索增强提示", "规则校验工作流"],
      novelty: 3,
      feasibility: 4,
      evidenceReadiness: 2,
      risks: ["合成场景代表性不足", "指标可能偏向规则系统"],
      pivotSignal:
        "若公开证据无法支持任务代表性，则收缩为系统可用性研究或更换应用场景。",
    },
  ];
  state.selectedDirectionId = "fictional-greenhouse-agent";
  state.experiment = {
    ...state.experiment,
    hypothesis:
      "待证伪：证据卡与约束门控能在同模型和同预算下减少不可执行建议，同时不显著增加不必要拒答。",
    taskDefinition:
      "输入告警、设备状态与手册片段，输出巡检步骤、证据引用和升级条件。",
    implementationStack:
      "Python；OpenAI-compatible API；模块化检索、校验和门控；所有组件可单独关闭",
    runMatrix:
      "3 个基线 × 3 个固定种子 × 正常/缺失/冲突三类场景；正式规模待预试验后冻结",
    datasets: "虚构演示：正式调研后选择公开数据，或公开合成协议与生成脚本",
    evaluationProtocol:
      "同输入、同模型、同 token/调用预算；开发集只调阈值，冻结后一次性运行测试集",
    primaryMetric: "可执行建议率，需由独立检查器与人工抽检共同定义",
    secondaryMetrics: "证据覆盖率、约束违规率、拒答适当性、延迟与调用成本",
    baselines: "直接提示、检索增强提示、规则校验工作流；正式基线待文献调研",
    mainComparison: "同模型、同输入、同预算下比较完整工作流与基线",
    ablations: "移除证据卡；移除约束校验；移除升级门控",
    hyperparameters: "检索条数、证据阈值、升级阈值",
    robustness: "缺失传感器、冲突手册片段、未见设备类型",
    caseStudy: "选择一例正确升级和一例错误建议，追踪证据与门控路径",
    computeBudget: "先运行小规模预试验；主实验规模由功效或稳定性分析决定",
    artifactPlan:
      "公开合成脚本、冻结配置、运行 manifest、逐样本 JSONL、汇总 CSV、独立绘图脚本和环境锁文件",
    reportingPlan:
      "主表比较可执行性与违规率；消融解释三个模块；鲁棒性展示缺失/冲突输入；案例追踪证据路径",
  };
  state.experiment.stopCriteria =
    "正式协议冻结后，主指标与风险指标共同支持核心主张；原始记录、逐题结果和绘图输入完整保存";
  state.paper.workingTitle =
    "Constraint-Aware Maintenance Agents for Small Greenhouses（虚构占位）";
  state.paper.centralClaim =
    "待验证：显式证据卡与约束门控可以提高维护建议的可执行性和可追溯性";
  state.paper.contributions =
    "虚构计划：任务定义；约束感知工作流；可审计评测协议。尚无实验结果或论文结论。";
  return state;
}
